/**
 * FeedbackPickerAddCell — the trailing "add" cell of the editable screenshots
 * grid on the add-feedback page ({@link FeedbackScreenshotsPickerGrid}).
 *
 * Rendered only while `length < 3`: a centered plus glyph that opens the
 * picker. Mirrors Flutter's `index == length` branch
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:445-460`);
 * the Flutter `_uploadingIndex` spinner is intentionally omitted (see the
 * {@link FeedbackScreenshotsPickerGrid} JSDoc web-deviation note).
 *
 * Pure presentational: owns no state, invokes no use case/service; the parent
 * grid supplies the add callback.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

const ADD_ICON_PATH = '/assets/images/add.png';

export interface FeedbackPickerAddCellProps {
  onAddTap: () => void;
}

export const FeedbackPickerAddCell = ({ onAddTap }: FeedbackPickerAddCellProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <button
      {...qaAttr({ id: 'feedback-picker-add' })}
      type="button"
      onClick={onAddTap}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Fill the square slot the parent VirtualList grid mode provides
        // (`itemAspectRatio`, `1`) rather than self-sizing via aspectRatio.
        width: '100%',
        height: '100%',
        border: 'none',
        borderRadius: responsive.borderRadius.sm,
        background: theme.secondary,
        cursor: 'pointer',
      }}
    >
      <AssetIcon
        path={ADD_ICON_PATH}
        width={responsive.icon.xlarge}
        height={responsive.icon.xlarge}
        color={theme.icon.primary}
      />
    </button>
  );
};
